/**
 * Consentement aux cookies (03/10/2026) : logique pure, sans React ni
 * navigateur, testée par tests/unit/consent.test.ts.
 *
 * Le choix du visiteur est gardé dans le stockage local, sous
 * `yp-consent-v2`, en JSON : une valeur par finalité soumise à accord, et la
 * date du choix. Il vaut 6 mois, la durée que la CNIL recommande de retenir
 * (accord comme refus) ; passé ce délai, le bandeau le redemande.
 *
 * L'ancienne clé `yp-consent` (17/09/2026 : « granted » ou « denied », sans
 * date) est ignorée et effacée : ce choix-là portait sur GTM et la publicité,
 * il ne vaut pas pour la mesure d'audience d'aujourd'hui.
 */

/**
 * Finalités soumises à accord. Une seule aujourd'hui. Pour ajouter la
 * publicité plus tard : « advertising » ici, son texte dans le bandeau, un
 * écran « Personnaliser » quand il y a plus d'une finalité, et ses outils
 * dans src/components/site/tracking.ts.
 */
export const CONSENT_PURPOSES = ["analytics"] as const;
export type ConsentPurpose = (typeof CONSENT_PURPOSES)[number];
export type ConsentChoices = Record<ConsentPurpose, boolean>;
export type ConsentRecord = ConsentChoices & { /** Date du choix, ISO 8601. */ date: string };

/** Clé du choix dans le stockage local. */
export const CONSENT_STORAGE_KEY = "yp-consent-v2";
/** Clés des versions précédentes, effacées à la lecture. */
export const LEGACY_CONSENT_KEYS = ["yp-consent"] as const;
/** Durée de validité du choix, en mois. */
export const CONSENT_VALIDITY_MONTHS = 6;
/** Ancre qui rouvre le bandeau (lien « Cookies » du pied de page, page Confidentialité). */
export const CONSENT_HASH = "#cookies";

/** Marge tolérée pour une date de choix dans le futur (horloge du visiteur décalée). */
const CLOCK_SKEW_MS = 24 * 60 * 60 * 1000;

type ConsentStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/**
 * Ajoute des mois calendaires, en UTC, sans déborder sur le mois suivant :
 * 31 août + 6 mois = 28 (ou 29) février, pas 3 mars.
 */
export function addMonths(date: Date, months: number): Date {
  const result = new Date(date.getTime());
  const day = result.getUTCDate();
  result.setUTCDate(1);
  result.setUTCMonth(result.getUTCMonth() + months);
  const lastDay = new Date(Date.UTC(result.getUTCFullYear(), result.getUTCMonth() + 1, 0)).getUTCDate();
  result.setUTCDate(Math.min(day, lastDay));
  return result;
}

/** Toutes les finalités à la même valeur (boutons « Accepter » et « Refuser »). */
export function allPurposes(value: boolean): ConsentChoices {
  return Object.fromEntries(CONSENT_PURPOSES.map((purpose) => [purpose, value])) as ConsentChoices;
}

/** Fin de validité d'un choix. */
export function consentExpiry(record: Pick<ConsentRecord, "date">): Date {
  return addMonths(new Date(record.date), CONSENT_VALIDITY_MONTHS);
}

/** Un choix vaut-il encore ? Daté, pas dans le futur, et de moins de 6 mois. */
export function isConsentValid(record: Pick<ConsentRecord, "date">, now: Date = new Date()): boolean {
  const date = new Date(record.date).getTime();
  if (Number.isNaN(date)) return false;
  if (date > now.getTime() + CLOCK_SKEW_MS) return false;
  return now.getTime() < consentExpiry(record).getTime();
}

/**
 * Lit la valeur stockée : un choix valide, ou `null` (rien, illisible,
 * incomplet ou périmé). Une finalité absente du JSON (ajoutée après le
 * choix) rend le choix incomplet : le bandeau le redemande.
 */
export function parseConsent(raw: string | null, now: Date = new Date()): ConsentRecord | null {
  if (!raw) return null;
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const value = data as Record<string, unknown>;
  if (typeof value.date !== "string") return null;
  if (!CONSENT_PURPOSES.every((purpose) => typeof value[purpose] === "boolean")) return null;
  const record = {
    ...(Object.fromEntries(CONSENT_PURPOSES.map((purpose) => [purpose, value[purpose]])) as ConsentChoices),
    date: value.date,
  };
  return isConsentValid(record, now) ? record : null;
}

/** Nouveau choix, daté de maintenant. */
export function createConsent(choices: ConsentChoices, now: Date = new Date()): ConsentRecord {
  return { ...choices, date: now.toISOString() };
}

/**
 * Choix en vigueur dans ce navigateur. Efface au passage les anciennes clés,
 * et la valeur courante si elle est illisible ou périmée.
 */
export function readConsent(storage: ConsentStorage, now: Date = new Date()): ConsentRecord | null {
  for (const key of LEGACY_CONSENT_KEYS) storage.removeItem(key);
  const raw = storage.getItem(CONSENT_STORAGE_KEY);
  const record = parseConsent(raw, now);
  if (raw !== null && !record) storage.removeItem(CONSENT_STORAGE_KEY);
  return record;
}

/** Enregistre un choix et le renvoie. */
export function writeConsent(storage: ConsentStorage, choices: ConsentChoices, now: Date = new Date()): ConsentRecord {
  const record = createConsent(choices, now);
  storage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
  return record;
}
